import { animator } from "../anim";
import { onResize } from "../resize";
import {
    CanvasMouseEventListener,
    CanvasMouseEventName,
    PaintFn,
} from "./interfaces";

export const touch = (canvas: HTMLCanvasElement) => {
    if (!canvas.parentElement) {
        throw new Error("Element is not inserted yet");
    }

    const ctx = canvas.getContext("2d");

    if (!ctx) {
        throw new Error("Couldn't get canvas' 2d context");
    }

    const px = {
        get dpr() {
            return window.devicePixelRatio;
        },
    };
    const container = canvas.parentElement;
    const listeners: Record<CanvasMouseEventName, CanvasMouseEventListener[]> =
        {
            down: [],
            up: [],
            move: [],
            dblclick: [],
        };
    const painters: PaintFn[] = [];
    let isMouseDown = false;
    let rect = canvas.getBoundingClientRect();

    const resize = () => {
        const { width, height } = container.getBoundingClientRect();

        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        canvas.width = width * px.dpr;
        canvas.height = height * px.dpr;

        animator.forceUpdate();
    };

    const notify = (name: CanvasMouseEventName, x: number, y: number) =>
        listeners[name].forEach((cb) => cb({ x, y, isDown: isMouseDown }));

    const getXY = (clientX: number, clientY: number) =>
        [
            (clientX - rect.left) * px.dpr,
            (clientY - rect.top) * px.dpr,
        ] as const;

    const listen = (...events: CanvasMouseEventName[]) => {
        if (events.includes("down")) {
            canvas.addEventListener("mousedown", ({ clientX, clientY }) => {
                isMouseDown = true;
                notify("down", ...getXY(clientX, clientY));
            });
        }

        if (events.includes("dblclick")) {
            canvas.addEventListener("dblclick", ({ clientX, clientY }) =>
                notify("dblclick", ...getXY(clientX, clientY)),
            );
        }

        if (events.includes("up")) {
            canvas.addEventListener("mouseup", ({ clientX, clientY }) => {
                isMouseDown = false;
                notify("up", ...getXY(clientX, clientY));
            });
        }

        if (events.includes("move")) {
            canvas.addEventListener("mousemove", ({ clientX, clientY }) =>
                notify("move", ...getXY(clientX, clientY)),
            );
            canvas.addEventListener("touchmove", (e) => {
                if (e.touches.length === 1) {
                    const touch = e.touches[0];

                    notify("move", ...getXY(touch.clientX, touch.clientY));
                }
            });
        }
    };

    const on = (
        eventName: CanvasMouseEventName,
        listener: CanvasMouseEventListener,
    ) => {
        listeners[eventName].push(listener);

        return self;
    };

    const get = () => ({ canvas, ctx });

    const paint = (painter: PaintFn) => {
        painters.push(painter);

        return self;
    };

    resize();

    const offResize = onResize(resize);

    const offUpdate = animator.onUpdate((updated) => {
        rect = canvas.getBoundingClientRect();

        if (updated) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            painters.forEach((cb) => cb(ctx, canvas));
        }
    });

    const destroy = () => {
        offResize();
        offUpdate();
    };

    const self = {
        on,
        get,
        paint,
        listen,
        destroy,
        get dpr() {
            return window.devicePixelRatio;
        },
    };

    return self;
};
